/**
 * The sidebar, 1:1 with the K3 shell (`k3-shell.new.js`): the order of each role's menu, the
 * bottom block, and the phone's four tabs. Moliyachi's Bronlar is the design's and the route
 * still refuses it (D615). Murojaatlar is no section: it is Suhbatlar' "Yangi" filter (D613).
 */
import {
  ChartColumn,
  House,
  MessageSquare,
  Search,
  Settings,
  SquareCheckBig,
  SquareKanban,
  Ticket,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type { ComponentType } from "react";

import type { Role } from "@/api/types";
import type { TranslationKey } from "@/i18n";
import { KapitanAiMark } from "@/ui/brand";

/** The sidebar's numbers (`GET /api/nav/counts/`). `murojaatlar` stays in the API's answer. */
export type NavCountKey = "murojaatlar" | "suhbatlar" | "bronlar" | "vazifalar";

export type NavKey =
  | "home"
  | "tasks"
  | "conversations"
  | "deals"
  | "bronlar"
  | "clients"
  | "tourSearch"
  | "aiAssistant"
  | "finance"
  | "reports"
  | "settings";

export interface NavItem {
  key: NavKey;
  labelKey: TranslationKey;
  shortKey?: TranslationKey | undefined;
  icon: LucideIcon | ComponentType<{ size?: number; className?: string }>;
  to: string;
  /** Other paths that belong to this section, for the active row. */
  aliases?: readonly string[] | undefined;
  countKey?: NavCountKey | undefined;
}

const ITEMS: Record<NavKey, NavItem> = {
  home: { key: "home", labelKey: "nav.home", shortKey: "nav.short.home", icon: House, to: "/" },
  tasks: { key: "tasks", labelKey: "nav.tasks", shortKey: "nav.short.tasks", icon: SquareCheckBig, to: "/tasks", countKey: "vazifalar" },
  conversations: {
    key: "conversations",
    labelKey: "nav.conversations",
    shortKey: "nav.short.conversations",
    icon: MessageSquare,
    to: "/inbox",
    countKey: "suhbatlar",
  },
  deals: { key: "deals", labelKey: "nav.deals", shortKey: "nav.short.deals", icon: SquareKanban, to: "/bitimlar" },
  bronlar: { key: "bronlar", labelKey: "nav.bronlar", shortKey: "nav.short.bronlar", icon: Ticket, to: "/bronlar", countKey: "bronlar" },
  clients: { key: "clients", labelKey: "nav.clients", shortKey: "nav.short.clients", icon: Users, to: "/mijozlar" },
  tourSearch: { key: "tourSearch", labelKey: "nav.tourSearch", icon: Search, to: "/tur-qidiruvi", aliases: ["/search"] },
  aiAssistant: {
    key: "aiAssistant",
    labelKey: "nav.aiAssistant",
    icon: KapitanAiMark,
    to: "/yordamchi",
    aliases: ["/automatizatsiya", "/kampaniyalar"],
  },
  finance: { key: "finance", labelKey: "nav.finance", shortKey: "nav.short.finance", icon: Wallet, to: "/moliya" },
  reports: { key: "reports", labelKey: "nav.reports", shortKey: "nav.short.reports", icon: ChartColumn, to: "/hisobotlar" },
  settings: {
    key: "settings",
    labelKey: "nav.settings",
    icon: Settings,
    to: "/sozlamalar",
    aliases: ["/shablonlar", "/hujjat-talablari", "/onboarding", "/ai"],
  },
};

/** An operator's report is their own: another label, another route (TZ §17.11). */
const MY_REPORT: NavItem = { ...ITEMS.reports, labelKey: "nav.myReport", to: "/hisobotlar/mening" };

const SALES_DESK: readonly NavKey[] = ["home", "tasks", "conversations", "deals", "bronlar", "clients", "tourSearch", "aiAssistant"];

const MAIN: Record<Role, readonly NavKey[]> = {
  operator: [...SALES_DESK, "reports"],
  branch_manager: [...SALES_DESK, "finance", "reports"],
  owner: [...SALES_DESK, "finance", "reports"],
  moliyachi: ["home", "finance", "bronlar", "clients", "reports"],
};

const BOTTOM: Record<Role, readonly NavKey[]> = {
  operator: [],
  branch_manager: ["settings"],
  owner: ["settings"],
  // TZ §17.14: the moliyachi's own two Sozlamalar screens; K3's menu has no row for them.
  moliyachi: ["settings"],
};

/** Phone (<640): four sections and "Menyu"; each role's four are its daily ones. */
const TABS: Record<Role, readonly NavKey[]> = {
  operator: ["home", "tasks", "conversations", "deals"],
  branch_manager: ["home", "conversations", "deals", "tasks"],
  owner: ["home", "deals", "finance", "reports"],
  moliyachi: ["home", "finance", "bronlar", "clients"],
};

function item(role: Role, key: NavKey): NavItem {
  return key === "reports" && role === "operator" ? MY_REPORT : ITEMS[key];
}

export function mainNav(role: Role): readonly NavItem[] {
  return MAIN[role].map((key) => item(role, key));
}

export function bottomNav(role: Role): readonly NavItem[] {
  return BOTTOM[role].map((key) => item(role, key));
}

export function mobileTabs(role: Role): readonly NavItem[] {
  return TABS[role].map((key) => item(role, key));
}

/** Whether this row is where the person is: the route, or a path that belongs to the section. */
export function isNavActive(navItem: NavItem, pathname: string): boolean {
  const matches = (to: string): boolean => (to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`));
  return matches(navItem.to) || (navItem.aliases ?? []).some(matches);
}

/** A thread open on a phone takes the whole screen: its composer sits where the bar would. */
export function hidesTabBar(pathname: string, search: string): boolean {
  if (pathname !== "/inbox") return false;
  const params = new URLSearchParams(search);
  return params.has("c") || params.has("cm");
}
